import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';
import { logEvent, getAnalytics } from 'firebase/analytics';
import { app } from '@/config/firebase';
import { TPA_REAUTH_PENDING_KEY } from '@/stores/module-store';
import { TPA_OAUTH_POPUP_KEY, TPA_REAUTH_WIPE_KEY } from '@/modules/tpa/tpa-reauth-cleanup';

type AppType = 'hubspot' | 'pipedrive' | 'slack' | 'generic';

const HUBSPOT_PAGES = [
  'Contacts detail page',
  'Deals detail page',
  'Tickets detail page',
  'Companies detail page',
];

const PIPEDRIVE_PAGES = [
  'Person detail page',
  'Deals detail page',
  'Organization detail page',
  'Person list page',
  'Deals list page',
  'Organization list page',
];

export function AuthorizationSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<string>('success');

  const appCode = searchParams.get('appCode') || '';
  const isAlreadyInstalled = Boolean(searchParams.get('isAlreadyInstalled'));
  const appUrl = searchParams.get('appUrl');

  const appType: AppType = useMemo(() => {
    if (appCode === 'twilioforslack') return 'slack';
    if (appCode.includes('forhubspot')) return 'hubspot';
    if (appCode.includes('forpipedrive')) return 'pipedrive';
    return 'generic';
  }, [appCode]);

  const appName = useMemo(() => {
    return appCode.split('for').shift() || 'App';
  }, [appCode]);

  useEffect(() => {
    // Determine status
    let resolvedStatus = searchParams.get('status') || 'success';
    if (searchParams.get('ua_service') === 'woocommerce') {
      resolvedStatus = searchParams.get('success') === '1' ? 'success' : 'failed';
    }
    setStatus(resolvedStatus);

    // Log analytics event
    if (resolvedStatus === 'success' && !isAlreadyInstalled) {
      try {
        const analytics = getAnalytics(app);
        logEvent(analytics, 'app_installed', { extensionName: appCode });
      } catch (e) {
        console.log('Analytics not available', e);
      }
    }

    // KAN-135: this popup's sessionStorage is a copy of the opener's from the
    // moment it was opened. A TPA re-auth stamp plus the pending flag means a
    // completed TPA re-auth — flag the history wipe on the opener now, for
    // EVERY app type (HubSpot/Pipedrive cards never reload the opener; their
    // app picks the flag up at its next boot).
    if (resolvedStatus === 'success') {
      try {
        const popupExt = sessionStorage.getItem(TPA_OAUTH_POPUP_KEY);
        const win = window.opener as Window | null;
        if (win && popupExt && sessionStorage.getItem(TPA_REAUTH_PENDING_KEY) === '1') {
          win.localStorage.setItem(TPA_REAUTH_WIPE_KEY, popupExt);
        }
      } catch { /* cross-origin opener or storage unavailable */ }
    }

    // Auto-redirect for generic apps on success
    if (resolvedStatus === 'success' && appType === 'generic') {
      try {
        gotoApp();
      } catch {
        window.close();
      }
    }
  }, [searchParams, appCode, isAlreadyInstalled, appType]);

  function gotoApp() {
    const win = window.opener as Window | null;
    if (!win) {
      if (appUrl) {
        window.location.href = appUrl;
      }
      return;
    }
    win.location.reload();
    window.close();
  }

  const isSuccess = status === 'success';

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md space-y-6">
        {/* App-specific success cards */}
        {isSuccess && appType === 'hubspot' && (
          <div className="flex flex-col items-center gap-5 rounded-xl border border-border bg-card p-8 shadow-sm">
            <CheckCircle className="h-16 w-16 text-success" />
            <h2 className="text-xl font-semibold text-foreground text-center capitalize">
              {appName} is installed in your HubSpot account!
            </h2>
            <div className="text-sm text-muted-foreground text-center">
              <p>You can now access app widgets on pages like</p>
              <ul className="mt-3 space-y-1 text-left list-disc pl-5">
                {HUBSPOT_PAGES.map((page) => (
                  <li key={page}>{page}</li>
                ))}
              </ul>
            </div>
            <a
              href="https://app.hubspot.com/"
              target="_self"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Go to HubSpot
            </a>
          </div>
        )}

        {isSuccess && appType === 'pipedrive' && (
          <div className="flex flex-col items-center gap-5 rounded-xl border border-border bg-card p-8 shadow-sm">
            <CheckCircle className="h-16 w-16 text-success" />
            <h2 className="text-xl font-semibold text-foreground text-center capitalize">
              {appName} is installed in your Pipedrive account!
            </h2>
            <div className="text-sm text-muted-foreground text-center">
              <p>You can now access app widgets on pages like</p>
              <ul className="mt-3 space-y-1 text-left list-disc pl-5">
                {PIPEDRIVE_PAGES.map((page) => (
                  <li key={page}>{page}</li>
                ))}
              </ul>
            </div>
            <a
              href="https://pipedrive.com/"
              target="_self"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Go to Pipedrive
            </a>
          </div>
        )}

        {isSuccess && appType === 'slack' && (
          <div className="flex flex-col items-center gap-5 rounded-xl border border-border bg-card p-8 shadow-sm text-center">
            <CheckCircle className="h-16 w-16 text-success" />
            <h2 className="text-xl font-semibold text-foreground">
              Twilio for Slack extension has been installed in your Slack Workspace
            </h2>
            <p className="text-sm text-muted-foreground">
              You can now access this app from Slack
            </p>
            <button
              onClick={gotoApp}
              className="inline-flex items-center rounded-lg bg-success px-6 py-2.5 text-sm font-medium text-white hover:bg-success/90 transition-colors"
            >
              Go to Application
            </button>
          </div>
        )}

        {isSuccess && appType === 'generic' && (
          <div className="flex flex-col items-center gap-5 rounded-xl border border-border bg-card p-8 shadow-sm text-center">
            <CheckCircle className="h-16 w-16 text-success" />
            <h2 className="text-xl font-semibold text-foreground">
              Your authorization is successful
            </h2>
            <p className="text-sm text-muted-foreground">
              This page will be closed automatically. Otherwise close this tab and go to previous page.
            </p>
          </div>
        )}

        {!isSuccess && (
          <div className="flex flex-col items-center gap-5 rounded-xl border border-border bg-card p-8 shadow-sm text-center">
            <XCircle className="h-16 w-16 text-destructive" />
            <h2 className="text-xl font-semibold text-foreground">
              Your authorization is {status}
            </h2>
            <p className="text-sm text-muted-foreground">
              This page will be closed automatically. Otherwise close this tab and go to previous page.
            </p>
          </div>
        )}

        {/* Bottom info banner */}
        <div className="rounded-lg border border-success/30 bg-success/10 p-3 text-center">
          <p className="text-sm font-medium text-success">
            You can now close this page and go to the application.
          </p>
        </div>
      </div>
    </div>
  );
}
